import { Box, Card, Divider, Grid, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import FieldContainer from '../../../FormFields/FieldContainer';
import FormButtonOptions from '../../../FormFields/FormButtonOptions';
import FormFileDrop from '../../../FormFields/FormFileDrop';
import FormMedicareWithFileDrop from '../../../FormFields/FormMedicareWithFileDrop';
import FormTextAreaField from '../../../FormFields/FormTextAreaField';
import FormTextField from '../../../FormFields/FormTextField';
import FormTitle from '../../../FormFields/FormTitle';
import FormVendorField from '../../../FormFields/FormVendorField';
import { FILE_TYPES } from '../../../formFileTypesEnum';
import { CLINICAL_REIMBURSEMENT } from '../../../formQuestionsEnum';
import { OUTSOURCED_COMPANY_CATEGORIES } from '../../../optionEnums';
import { formCardStyle } from '../../../TransitionStyles';

MedicareCertificationsForm.propTypes = {
  permissions: PropTypes.object,
};

export default function MedicareCertificationsForm({ permissions }) {
  const [focusedField, setFocusedField] = useState(null);
  const { watch } = useFormContext();
  const {
    data: { outsourcedCompanyCategoriesList },
  } = useSelector((state) => state.transitions);
  const rehabOutsourcedCompanyCategoryId = watch(CLINICAL_REIMBURSEMENT.REHAB_OUTSOURCED_COMPANY_CATEGORY_ID.fieldName);
  const shouldShowRehabVendor = [
    OUTSOURCED_COMPANY_CATEGORIES.CONTRACTED,
    OUTSOURCED_COMPANY_CATEGORIES.IN_HOUSE_CONTRACT_MANAGEMENT,
  ].includes(String(rehabOutsourcedCompanyCategoryId));

  const fieldIds = [
    CLINICAL_REIMBURSEMENT.SELLER_MEDICARE_NUMBER.id,
    CLINICAL_REIMBURSEMENT.MEDICARE_A_CERTIFICATION_FILE.id,
    CLINICAL_REIMBURSEMENT.MDS_SCRUBBER.id,
    CLINICAL_REIMBURSEMENT.MDS_SUBMISSION_PROCESS.id,
    CLINICAL_REIMBURSEMENT.REHAB_OUTSOURCED_COMPANY_CATEGORY_ID.id,
    CLINICAL_REIMBURSEMENT.ICD10_PROCESS.id,
    CLINICAL_REIMBURSEMENT.CASE_MANAGEMENT_PROCESS.id,
    CLINICAL_REIMBURSEMENT.TRIPLE_CHECK_PROCESS.id,
    CLINICAL_REIMBURSEMENT.EMR_SOFTWARE_VENDOR.id,
    CLINICAL_REIMBURSEMENT.REHAB_VENDOR.id,
  ];

  const showForm = fieldIds.some((id) => id in permissions);

  return (
    showForm && (
      <Card sx={formCardStyle}>
        <Typography sx={{ mb: 1 }} align="center" variant="body1" color="info.main">
          Medicare Certifications
        </Typography>
        <Divider sx={{ mb: 1, mt: 2, borderWidth: 1 }} />
        <Grid container spacing={1.5}>
          {CLINICAL_REIMBURSEMENT.SELLER_MEDICARE_NUMBER.id in permissions && (
            <Grid item xs={12}>
              <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                <Box mb={1}>
                  <FormTitle fieldAttributes={{ questionTitle: 'Seller medicare number', fieldType: 'file' }} />
                </Box>
                <FormMedicareWithFileDrop
                  fieldName={CLINICAL_REIMBURSEMENT.SELLER_MEDICARE_NUMBER.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[CLINICAL_REIMBURSEMENT.SELLER_MEDICARE_NUMBER.id],
                    fileTypeId: FILE_TYPES.SELLER_MEDICARE_NUMBER,
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </Box>
            </Grid>
          )}
          {CLINICAL_REIMBURSEMENT.MEDICARE_A_CERTIFICATION_FILE.id in permissions && (
            <Grid item xs={12}>
              <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                <FormTitle
                  fieldAttributes={{ questionTitle: 'Medicare certifications (Medicare A)', fieldType: 'file' }}
                />
                <FormFileDrop
                  fieldName={CLINICAL_REIMBURSEMENT.MEDICARE_A_CERTIFICATION_FILE.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[CLINICAL_REIMBURSEMENT.MEDICARE_A_CERTIFICATION_FILE.id],
                    fileTypeId: FILE_TYPES.MEDICARE_A_CERTIFICATION,
                  }}
                />
              </Box>
            </Grid>
          )}
          {CLINICAL_REIMBURSEMENT.EMR_SOFTWARE_VENDOR.id in permissions && (
            <Grid item xs={12}>
              <FormTitle fieldAttributes={{ questionTitle: 'EMR software vendor', fieldType: 'title' }} />

              <FormVendorField
                fieldName={CLINICAL_REIMBURSEMENT.EMR_SOFTWARE_VENDOR.fieldName}
                fieldAttributes={{ isEditable: permissions[CLINICAL_REIMBURSEMENT.EMR_SOFTWARE_VENDOR.id] }}
                invoiceFileTypeId={FILE_TYPES.EMR_SOFTWARE_VENDOR_INVOICE}
                contractFileTypeId={FILE_TYPES.EMR_SOFTWARE_VENDOR_CONTRACT}
              />
            </Grid>
          )}
          {CLINICAL_REIMBURSEMENT.MDS_SCRUBBER.id in permissions && (
            <Grid item xs={12}>
              <FormTitle fieldAttributes={{ questionTitle: 'MDS scrubber' }} />
              <FormTextField
                fieldName={CLINICAL_REIMBURSEMENT.MDS_SCRUBBER.fieldName}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                fieldAttributes={{
                  isEditable: permissions[CLINICAL_REIMBURSEMENT.MDS_SCRUBBER.id],
                }}
              />
            </Grid>
          )}
          {CLINICAL_REIMBURSEMENT.MDS_SUBMISSION_PROCESS.id in permissions && (
            <Grid item xs={12}>
              <FormTitle fieldAttributes={{ questionTitle: 'MDS submission process' }} />
              <FormTextAreaField
                fieldName={CLINICAL_REIMBURSEMENT.MDS_SUBMISSION_PROCESS.fieldName}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                fieldAttributes={{ isEditable: permissions[CLINICAL_REIMBURSEMENT.MDS_SUBMISSION_PROCESS.id] }}
                placeholder="List out the process your facility uses"
              />
            </Grid>
          )}
          {CLINICAL_REIMBURSEMENT.REHAB_OUTSOURCED_COMPANY_CATEGORY_ID.id in permissions && (
            <Grid item xs={12}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Rehab' }} />
                <FormButtonOptions
                  fieldName={CLINICAL_REIMBURSEMENT.REHAB_OUTSOURCED_COMPANY_CATEGORY_ID.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[CLINICAL_REIMBURSEMENT.REHAB_OUTSOURCED_COMPANY_CATEGORY_ID.id],
                    options: outsourcedCompanyCategoriesList.filter((x) =>
                      [
                        OUTSOURCED_COMPANY_CATEGORIES.CONTRACTED,
                        OUTSOURCED_COMPANY_CATEGORIES.IN_HOUSE,
                        OUTSOURCED_COMPANY_CATEGORIES.IN_HOUSE_CONTRACT_MANAGEMENT,
                      ].includes(x.value)
                    ),
                  }}
                />
              </FieldContainer>
            </Grid>
          )}
          {CLINICAL_REIMBURSEMENT.REHAB_VENDOR.id in permissions && shouldShowRehabVendor && (
            <Grid item xs={12}>
              <FormTitle fieldAttributes={{ questionTitle: 'Rehab vendor', fieldType: 'title' }} />
              <FormVendorField
                fieldName={CLINICAL_REIMBURSEMENT.REHAB_VENDOR.fieldName}
                fieldAttributes={{ isEditable: permissions[CLINICAL_REIMBURSEMENT.REHAB_VENDOR.id] }}
                invoiceFileTypeId={FILE_TYPES.REHAB_VENDOR_INVOICE}
                contractFileTypeId={FILE_TYPES.REHAB_VENDOR_CONTRACT}
              />
            </Grid>
          )}
          {CLINICAL_REIMBURSEMENT.ICD10_PROCESS.id in permissions && (
            <Grid item xs={12}>
              <FormTitle fieldAttributes={{ questionTitle: 'ICD-10 process' }} />
              <FormTextAreaField
                fieldName={CLINICAL_REIMBURSEMENT.ICD10_PROCESS.fieldName}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                fieldAttributes={{ isEditable: permissions[CLINICAL_REIMBURSEMENT.ICD10_PROCESS.id] }}
                placeholder="List out the process your facility uses"
              />
            </Grid>
          )}
          {CLINICAL_REIMBURSEMENT.CASE_MANAGEMENT_PROCESS.id in permissions && (
            <Grid item xs={12}>
              <FormTitle fieldAttributes={{ questionTitle: 'Case management process' }} />
              <FormTextAreaField
                fieldName={CLINICAL_REIMBURSEMENT.CASE_MANAGEMENT_PROCESS.fieldName}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                fieldAttributes={{ isEditable: permissions[CLINICAL_REIMBURSEMENT.CASE_MANAGEMENT_PROCESS.id] }}
                placeholder="List out the process your facility uses"
              />
            </Grid>
          )}
          {CLINICAL_REIMBURSEMENT.TRIPLE_CHECK_PROCESS.id in permissions && (
            <Grid item xs={12}>
              <FormTitle fieldAttributes={{ questionTitle: 'Triple check process' }} />
              <FormTextAreaField
                fieldName={CLINICAL_REIMBURSEMENT.TRIPLE_CHECK_PROCESS.fieldName}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                fieldAttributes={{ isEditable: permissions[CLINICAL_REIMBURSEMENT.TRIPLE_CHECK_PROCESS.id] }}
                placeholder="List out the process your facility uses"
              />
            </Grid>
          )}
        </Grid>
      </Card>
    )
  );
}
